/**
 * Sparkie — right-docked (or popped-out, draggable) in-app chat panel over the
 * Salesforce MIAW API.
 *
 * A thin React view over MiawSession (transport: token, conversation, SSE).
 * Non-modal; can pop out into a draggable window; keeps a per-user local history
 * of chats in localStorage (browser-only, no server list).
 *
 * - Lazy: nothing connects until first opened.
 * - Continuity: the active conversationId is persisted; on reload it resumes and
 *   history is rehydrated from the MIAW /entries list.
 * - `sessionFactory` is injectable for unit tests.
 */

import React from 'react';
import styles from './Sparkie.module.css';
import renderAgentMarkdown from './SparkieMarkdown.js';
import { getSession } from 'Vulture/Session.js';
import { resolveChatIdentity, fetchSparkieIdentityToken } from 'Vulture/lib/miaw/identity.js';
import MiawSession from 'Vulture/lib/miaw/MiawSession.js';
import MiawClient from 'Vulture/lib/miaw/MiawClient.js';
import { MIAW_CHANNEL_VARS, MIAW_CONFIG } from 'Vulture/lib/miaw/config.js';

const STORAGE_VERSION = 1;
const FLOAT_W = 380;
const FLOAT_H = 560;
const ARCHIVE_CAP = 30;

// Header-button icons — all one size (24 viewBox, 18px) so they read consistently.
const ICONS = {
	menu: 'M4 7h16M4 12h16M4 17h16',
	plus: 'M12 5v14M5 12h14',
	expand: 'M9 4H4v5M4 4l6 6M15 20h5v-5M20 20l-6-6',
	collapse: 'M20 9V4h-5M20 4l-6 6M4 15v5h5M4 20l6-6',
	close: 'M6 6l12 12M6 18L18 6',
};

const HeaderIcon = ({ d }) => (
	<svg viewBox='0 0 24 24' width='18' height='18' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
		<path d={d} />
	</svg>
);

class Sparkie extends React.Component {
	state = {
		messages: [],
		status: 'idle',
		input: '',
		typing: false,
		stalled: false, // no reply within the session's watchdog window
		pos: null, // null = docked; { x, y } = floating window
		showHistory: false,
		viewingChat: null, // a past chat being read (read-only), or null for the live chat
		everOpened: false, // latched on first open; content stays mounted after
	};

	// Until first opened, render an empty shell: the panel's text (greeting,
	// buttons) must not exist in the DOM for sessions that never use Sparkie —
	// page-level text selectors (e2e getByText is substring matching) would
	// otherwise see it on every logged-in page.
	static getDerivedStateFromProps(props, state) {
		if (props.open && !state.everOpened) {
			return { everOpened: true };
		}
		return null;
	}

	session = null;
	sharedClient = null; // one MiawClient (one token/uid) for the panel's lifetime
	unsubscribe = null;
	started = false;
	userUUID = null;
	panelRef = React.createRef();
	listEndRef = React.createRef();
	dragOffset = null;

	componentDidMount() {
		const session = (typeof getSession === 'function' && getSession()) || {};
		this.userUUID = session.user || 'anonymous';
		if (this.props.open) {
			this.ensureStarted();
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.props.open && !prevProps.open) {
			this.ensureStarted();
		}
		if (this.state.messages.length !== prevState.messages.length
			|| (this.state.typing && !prevState.typing)
			|| (this.state.stalled && !prevState.stalled)) {
			this.scrollToBottom();
		}
	}

	componentWillUnmount() {
		if (this.unsubscribe) {
			this.unsubscribe();
			this.unsubscribe = null;
		}
		this.endDrag();
		if (this.session) {
			this.session.detach();
		}
	}

	/* ---------- persistence: active id + per-user chat archive ---------- */

	activeKey() {
		return `sparkie:v${STORAGE_VERSION}:${this.userUUID}`;
	}

	archiveKey() {
		return `sparkie:v${STORAGE_VERSION}:${this.userUUID}:chats`;
	}

	loadConversationId() {
		try {
			return localStorage.getItem(this.activeKey()) || null;
		} catch (err) {
			return null;
		}
	}

	persistConversationId() {
		if (!this.session || !this.session.conversationId) {
			return;
		}
		try {
			localStorage.setItem(this.activeKey(), this.session.conversationId);
		} catch (err) {
			/* best-effort */
		}
	}

	clearConversationId() {
		try {
			localStorage.removeItem(this.activeKey());
		} catch (err) {
			/* no-op */
		}
	}

	loadArchive() {
		try {
			const chats = JSON.parse(localStorage.getItem(this.archiveKey()) || '[]') || [];
			// Chats archived before text coercion existed can hold non-string text
			// (rich payloads passed through) — drop those bubbles so the entry stays
			// viewable instead of tripping the error boundary on every open.
			return chats.map((c) => ({
				...c,
				messages: (c.messages || []).filter((m) => m && typeof m.text === 'string'),
			}));
		} catch (err) {
			return [];
		}
	}

	saveArchive(chats) {
		try {
			localStorage.setItem(this.archiveKey(), JSON.stringify(chats.slice(0, ARCHIVE_CAP)));
		} catch (err) {
			/* best-effort (quota) */
		}
	}

	deriveTitle(messages) {
		const firstUser = (messages || []).find((m) => m && m.from === 'user' && m.text);
		if (firstUser) {
			const t = firstUser.text.replace(/\s+/g, ' ').trim();
			return t.length > 48 ? `${t.slice(0, 48)}…` : t;
		}
		return 'New chat';
	}

	// Keep the current conversation in the local archive (upsert by conversationId),
	// but only once the user has actually said something.
	archiveCurrent() {
		if (!this.session || !this.session.conversationId) {
			return;
		}
		const messages = this.state.messages;
		if (!messages.some((m) => m && m.from === 'user')) {
			return;
		}
		const entry = {
			conversationId: this.session.conversationId,
			title: this.deriveTitle(messages),
			messages,
			updatedAt: Date.now(),
		};
		const rest = this.loadArchive().filter((c) => c.conversationId !== entry.conversationId);
		this.saveArchive([entry, ...rest]);
	}

	/* ---------- session lifecycle ---------- */

	// One shared client (token/uid) for the whole panel, so conversations created
	// this session can be resumed/continued (a new token would 403 on them).
	// Persist the access token per user (sessionStorage survives reloads), so a
	// reload keeps the same anonymous uid and earlier chats remain resumable.
	tokenStore() {
		// Namespace by auth mode: an unauthenticated token (anonymous, per-mint uid)
		// and an authenticated token (stable identity uid) are NOT interchangeable —
		// reusing one across modes points the client at the wrong identity and skips
		// the mint flow entirely. Keep a separate bucket per mode.
		const mode = MIAW_CONFIG.authenticated ? 'auth' : 'anon';
		const key = `sparkie:v${STORAGE_VERSION}:${mode}:${this.userUUID}:token`;
		return {
			load: () => {
				try {
					return JSON.parse(sessionStorage.getItem(key) || 'null');
				} catch (err) {
					return null;
				}
			},
			save: (token, expiresAt) => {
				try {
					sessionStorage.setItem(key, JSON.stringify({ token, expiresAt }));
				} catch (err) {
					/* no-op */
				}
			},
		};
	}

	getClient() {
		if (!this.sharedClient) {
			const opts = { tokenStore: this.tokenStore() };
			if (MIAW_CONFIG.authenticated) {
				// Authenticated flow: exchange a bronco-signed identity JWT for the
				// access token, giving a stable uid so conversations persist.
				opts.identityTokenProvider = () => {
					return fetchSparkieIdentityToken((typeof getSession === 'function' && getSession()) || null);
				};
			}
			this.sharedClient = new MiawClient(MIAW_CONFIG, opts);
		}
		return this.sharedClient;
	}

	makeSession() {
		if (this.props.sessionFactory) {
			return this.props.sessionFactory();
		}
		return new MiawSession({ client: this.getClient() });
	}

	teardownLive() {
		if (this.unsubscribe) {
			this.unsubscribe();
			this.unsubscribe = null;
		}
		if (this.session) {
			this.session.detach();
			this.session = null;
		}
		this.started = false;
	}

	async buildRoutingAttributes() {
		const session = (typeof getSession === 'function' && getSession()) || null;
		const identity = await resolveChatIdentity(session);
		const meta = this.props.metadata || {};
		return {
			[MIAW_CHANNEL_VARS.firstName]: identity.firstName,
			[MIAW_CHANNEL_VARS.lastName]: identity.lastName,
			[MIAW_CHANNEL_VARS.email]: identity.email,
			pageMode: meta.mode || '',
			accountName: meta.accountName || '',
			accountUUID: meta.accountUUID || '',
		};
	}

	async startFresh() {
		const routingAttributes = await this.buildRoutingAttributes();
		await this.session.start(routingAttributes);
		this.persistConversationId();
	}

	subscribeSession() {
		this.unsubscribe = this.session.subscribe((snapshot) => {
			this.setState(
				{ messages: snapshot.messages, status: snapshot.status, typing: !!snapshot.typing, stalled: !!snapshot.stalled },
				() => this.archiveCurrent()
			);
		});
	}

	async ensureStarted() {
		if (this.started) {
			return;
		}
		this.started = true;

		if (!MIAW_CONFIG.esDeveloperName) {
			this.setState({ status: 'unconfigured' });
			return;
		}

		this.session = this.makeSession();
		this.subscribeSession();

		const priorConversationId = this.loadConversationId();
		try {
			if (priorConversationId) {
				await this.session.resume(priorConversationId);
			} else {
				await this.startFresh();
			}
		} catch (err) {
			this.clearConversationId();
			try {
				await this.startFresh();
			} catch (err2) {
				console.error('Sparkie failed to start a conversation', err2);
			}
		}
	}

	// New chat: archive the current one and start fresh (detach, don't end — so a
	// past chat can still be reopened/continued from history).
	newChat = async () => {
		this.archiveCurrent();
		this.teardownLive();
		this.clearConversationId();
		this.setState({ messages: [], status: 'idle', typing: false, stalled: false, input: '', showHistory: false, viewingChat: null });
		await this.ensureStarted();
	};

	// Reopen a past chat. Conversations created THIS session share the token/uid
	// and can be resumed live to continue. Ones from a prior browser session can't
	// (403 — see KB), so they open read-only.
	openChat = async (chat) => {
		if (this.session && chat.conversationId === this.session.conversationId) {
			this.setState({ showHistory: false, viewingChat: null });
			return;
		}
		this.archiveCurrent();
		this.teardownLive();
		// Instant paint from the stored copy while we resume.
		this.setState({ showHistory: false, viewingChat: null, messages: chat.messages || [], typing: false, stalled: false, status: 'connecting' });

		this.session = this.makeSession();
		this.subscribeSession();
		this.started = true;
		try {
			await this.session.resume(chat.conversationId);
			try {
				localStorage.setItem(this.activeKey(), chat.conversationId);
			} catch (err) {
				/* no-op */
			}
		} catch (err) {
			// Not resumable (older session / expired) — show it read-only.
			this.teardownLive();
			this.setState({ viewingChat: chat, messages: chat.messages || [], status: 'idle' });
		}
	};

	deleteChat = (conversationId, e) => {
		if (e && e.stopPropagation) {
			e.stopPropagation();
		}
		this.saveArchive(this.loadArchive().filter((c) => c.conversationId !== conversationId));
		this.forceUpdate();
	};

	toggleHistory = () => {
		this.setState((s) => ({ showHistory: !s.showHistory }));
	};

	/* ---------- pop-out / drag ---------- */

	togglePopout = () => {
		if (this.state.pos) {
			this.setState({ pos: null });
			return;
		}
		const x = Math.max(0, window.innerWidth - FLOAT_W - 40);
		this.setState({ pos: { x, y: 80 } });
	};

	onHeaderMouseDown = (e) => {
		if (e.target.closest && e.target.closest('button')) {
			return;
		}
		const panel = this.panelRef.current;
		const rect = panel ? panel.getBoundingClientRect() : { left: 0, top: 0 };
		this.dragOffset = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
		this.setState({ pos: { x: rect.left, y: rect.top } });
		// Body-level so the grab cursor holds even when the pointer outruns the header.
		document.body.style.cursor = 'grabbing';
		document.addEventListener('mousemove', this.onDragMove);
		document.addEventListener('mouseup', this.endDrag);
		e.preventDefault();
	};

	onDragMove = (e) => {
		if (!this.dragOffset) {
			return;
		}
		const x = Math.max(0, Math.min(window.innerWidth - FLOAT_W, e.clientX - this.dragOffset.dx));
		const y = Math.max(0, Math.min(window.innerHeight - 48, e.clientY - this.dragOffset.dy));
		this.setState({ pos: { x, y } });
	};

	endDrag = () => {
		this.dragOffset = null;
		document.body.style.cursor = '';
		document.removeEventListener('mousemove', this.onDragMove);
		document.removeEventListener('mouseup', this.endDrag);
	};

	/* ---------- composer ---------- */

	onInputChange = (e) => {
		this.setState({ input: e.target.value });
	};

	onSubmit = (e) => {
		if (e && e.preventDefault) {
			e.preventDefault();
		}
		const text = this.state.input.trim();
		if (!text || !this.session) {
			return;
		}
		this.setState({ input: '' });
		Promise.resolve(this.session.send(text)).catch((err) => {
			console.error('Sparkie send failed', err);
		});
	};

	scrollToBottom() {
		const el = this.listEndRef.current;
		if (el && typeof el.scrollIntoView === 'function') {
			el.scrollIntoView({ block: 'end' });
		}
	}

	statusLabel() {
		switch (this.state.status) {
			case 'connecting':
				return 'Connecting…';
			case 'reconnecting':
				return 'Reconnecting…';
			case 'error':
				return 'Connection error';
			case 'unconfigured':
				return 'Preview — not connected yet';
			default:
				return '';
		}
	}

	renderHistory() {
		const chats = this.loadArchive();
		return (
			<div className={styles.history} data-test-id='sparkieHistory'>
				{chats.length === 0 ? (
					<div className={styles.historyEmpty}>No previous chats yet.</div>
				) : (
					chats.map((c) => (
						<div key={c.conversationId} className={styles.historyRow} data-test-id='sparkieHistoryRow' onClick={() => this.openChat(c)}>
							<div className={styles.historyMain}>
								<div className={styles.historyTitle}>{c.title || 'New chat'}</div>
								<div className={styles.historyMeta}>{new Date(c.updatedAt).toLocaleString()}</div>
							</div>
							<button
								type='button'
								className={styles.historyDelete}
								aria-label='Delete chat'
								title='Delete'
								onClick={(e) => this.deleteChat(c.conversationId, e)}
							>
								×
							</button>
						</div>
					))
				)}
			</div>
		);
	}

	render() {
		const { open, onClose } = this.props;
		const { messages, input, typing, stalled, pos, showHistory, viewingChat, everOpened } = this.state;

		if (!everOpened) {
			return (
				<aside
					ref={this.panelRef}
					className={`${styles.panel} ${styles.closed}`}
					aria-hidden='true'
					aria-label='Sparkie assistant'
				/>
			);
		}
		const status = this.statusLabel();
		const floating = !!pos;
		const shownMessages = viewingChat ? (viewingChat.messages || []) : messages;
		const floatStyle = floating
			? { left: `${pos.x}px`, top: `${pos.y}px`, width: `${FLOAT_W}px`, height: `${FLOAT_H}px` }
			: undefined;

		return (
			<aside
				ref={this.panelRef}
				className={`${styles.panel} ${open ? styles.open : styles.closed} ${floating ? styles.floating : ''}`}
				style={floatStyle}
				aria-hidden={open ? 'false' : 'true'}
				aria-label='Sparkie assistant'
			>
				<header className={styles.header} onMouseDown={this.onHeaderMouseDown}>
					<span className={styles.title}>Sparkie</span>
					{status ? <span className={styles.status}>{status}</span> : null}
					<div className={styles.headerActions}>
						<button type='button' className={styles.headerBtn} aria-label='Previous chats' title='Previous chats' onClick={this.toggleHistory}>
							<HeaderIcon d={ICONS.menu} />
						</button>
						<button type='button' className={styles.headerBtn} aria-label='New chat' title='New chat' onClick={this.newChat}>
							<HeaderIcon d={ICONS.plus} />
						</button>
						<button type='button' className={styles.headerBtn} aria-label={floating ? 'Dock Sparkie' : 'Pop out Sparkie'} title={floating ? 'Dock' : 'Pop out'} onClick={this.togglePopout}>
							<HeaderIcon d={floating ? ICONS.collapse : ICONS.expand} />
						</button>
						<button type='button' className={styles.headerBtn} aria-label='Close Sparkie' title='Close' onClick={onClose}>
							<HeaderIcon d={ICONS.close} />
						</button>
					</div>
				</header>

				{showHistory ? this.renderHistory() : null}

				<div className={styles.messages}>
					{viewingChat ? (
						<div className={styles.viewingBanner}>This chat is from an earlier session — read-only</div>
					) : null}
					{!viewingChat && shownMessages.length === 0 ? (
						<div className={styles.greeting}>
							Hi! I&rsquo;m Sparkie! Your TouchSource digital signage AI assistant. I can
							answer questions about your portal, products, content management, cleaning and
							maintenance, and Windows 10 updates. If you have a system issue or need to reach
							our team, I can also create a support case for you. What can I help you with today?
						</div>
					) : (
						shownMessages.map((m, i) => (
							<div
								key={m.id || i}
								className={`${styles.message} ${m.from === 'user' ? styles.fromUser : styles.fromAgent} ${m.failed ? styles.failed : ''}`}
							>
								{m.from === 'user' ? m.text : renderAgentMarkdown(m.text)}
							</div>
						))
					)}
					{typing && !viewingChat ? (
						<div className={styles.typing} data-test-id='sparkieTyping' aria-label='Sparkie is typing'>
							<span />
							<span />
							<span />
						</div>
					) : null}
					{stalled && !typing && !viewingChat ? (
						<div className={styles.stalledNotice} data-test-id='sparkieStalled'>
							Sparkie hasn&rsquo;t responded &mdash; if you were being connected to a
							representative, this can take a while. You can keep waiting, send your
							question again, or start a new chat from the + button above.
						</div>
					) : null}
					<div ref={this.listEndRef} />
				</div>

				{viewingChat ? (
					<div className={styles.viewerBar}>
						<button type='button' className={styles.backBtn} onClick={this.newChat}>
							Start a new chat
						</button>
					</div>
				) : (
					<form className={styles.composer} onSubmit={this.onSubmit}>
						<input
							type='text'
							className={styles.input}
							placeholder='Type your message…'
							value={input}
							onChange={this.onInputChange}
							aria-label='Message Sparkie'
						/>
						<button type='submit' className={styles.send} aria-label='Send' disabled={!input.trim()}>
							<svg viewBox='0 0 24 24' width='32' height='32' className={styles.sendIcon} fill='currentColor' aria-hidden='true'>
								<path d='M2 21l21-9L2 3v7l15 2-15 2v7z' />
							</svg>
						</button>
					</form>
				)}
			</aside>
		);
	}
}

// Error boundary: a render/lifecycle throw inside the panel (e.g. an unexpected
// reply shape) must degrade to a fallback INSIDE the panel, never unmount the
// whole dashboard. Restart remounts fresh (keyed) — the active conversationId is
// in localStorage, so the chat resumes.
class SparkieSafe extends React.Component {
	state = { crashed: false, epoch: 0 };

	static getDerivedStateFromError() {
		return { crashed: true };
	}

	componentDidCatch(error, info) {
		console.error('Sparkie crashed:', error, info && info.componentStack);
	}

	restart = () => {
		this.setState((s) => ({ crashed: false, epoch: s.epoch + 1 }));
	};

	render() {
		if (!this.state.crashed) {
			return <Sparkie key={this.state.epoch} {...this.props} />;
		}
		const { open, onClose } = this.props;
		return (
			<aside
				className={`${styles.panel} ${open ? styles.open : styles.closed}`}
				aria-hidden={open ? 'false' : 'true'}
				aria-label='Sparkie assistant'
			>
				<header className={styles.header}>
					<span className={styles.title}>Sparkie</span>
					<div className={styles.headerActions}>
						<button type='button' className={styles.headerBtn} aria-label='Close Sparkie' title='Close' onClick={onClose}>
							<HeaderIcon d={ICONS.close} />
						</button>
					</div>
				</header>
				<div className={styles.messages}>
					<div className={styles.greeting}>
						Sparkie ran into a problem and had to stop. Your conversation is saved
						&mdash; restart to pick it back up.
					</div>
				</div>
				<div className={styles.viewerBar}>
					<button type='button' className={styles.backBtn} onClick={this.restart}>
						Restart Sparkie
					</button>
				</div>
			</aside>
		);
	}
}

export default SparkieSafe;
